"use client";

import React, { useState, useMemo } from "react";
import { FileText, ShieldCheck, ArrowRight, Search, Thermometer, Layers, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DataTable, { type DataTableColumn } from "@/components/common/DataTable";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface AlloySpecification {
  id: string;
  grade: string;
  unsCode: string;
  dinCode: string;
  category: "standard" | "chemical" | "hightemp" | "specialty";
  composition: string;
  corrosionRating: Record<Lang, string>;
  corrosionLevel: "Good" | "Excellent" | "Superior" | "Exceptional";
  acidResistance: Record<Lang, string>;
  chlorideResistance: Record<Lang, string>;
  applications: Record<Lang, string[]>;
  badgeText: Record<Lang, string>;
}
const alloyData: AlloySpecification[] = [{
  id: "304",
  grade: "AISI 304 / 304L",
  unsCode: "S30400 / S30403",
  dinCode: "1.4301 / 1.4307",
  category: "standard",
  composition: "18% Cr, 8% Ni",
  corrosionLevel: "Good",
  corrosionRating: {
    en: "Good",
    zh: "良好",
    es: "Bueno",
    de: "Gut",
    fr: "Bon",
    pt: "Bom"
  },
  acidResistance: {
    en: "Moderate (Organic acids)",
    zh: "中等（有机酸）",
    es: "Moderada (Ácidos orgánicos)",
    de: "Moderat (Organische Säuren)",
    fr: "Modérée (Acides organiques)",
    pt: "Moderada (Ácidos orgânicos)"
  },
  chlorideResistance: {
    en: "Fair (Freshwater, mild atmospheric)",
    zh: "一般（淡水、常温大气环境）",
    es: "Aceptable (Agua dulce, atmosférico leve)",
    de: "Mäßig (Süßwasser, milde Atmosphäre)",
    fr: "Passable (Eau douce, atmosphère modérée)",
    pt: "Aceitável (Água doce, atmosférico brando)"
  },
  applications: {
    en: ["General liquid straining", "Air intake filtration", "Food & beverage screens", "Architectural mesh"],
    zh: ["常规液体截留过滤", "进风口滤尘滤网", "食品饮料加工筛", "建筑装饰网"],
    es: ["Colado general de líquidos", "Filtración de admisión de aire", "Tamices de alimentos y bebidas", "Malla arquitectónica"],
    de: ["Allgemeine Flüssigkeitsfiltration", "Lufteinlassfiltration", "Siebe für Lebensmittel & Getränke", "Architekturgewebe"],
    fr: ["Filtration générale de liquides", "Filtration d'admission d'air", "Tamis alimentaires et boissons", "Toile architecturale"],
    pt: ["Filtragem geral de líquidos", "Filtração de admissão de ar", "Peneiras para alimentos e bebidas", "Telas arquitetônicas"]
  },
  badgeText: {
    en: "Industry Standard",
    zh: "工业标准型",
    es: "Estándar industrial",
    de: "Industriestandard",
    fr: "Standard industriel",
    pt: "Padrão industrial"
  }
}, {
  id: "316",
  grade: "AISI 316 / 316L",
  unsCode: "S31600 / S31603",
  dinCode: "1.4401 / 1.4404",
  category: "chemical",
  composition: "16-18% Cr, 10-14% Ni, 2-3% Mo",
  corrosionLevel: "Excellent",
  corrosionRating: {
    en: "Excellent",
    zh: "优良",
    es: "Excelente",
    de: "Ausgezeichnet",
    fr: "Excellent",
    pt: "Excelente"
  },
  acidResistance: {
    en: "High (Phosphoric, acetic, diluted sulfuric)",
    zh: "优异（磷酸、醋酸、稀硫酸）",
    es: "Alta (Fosfórico, acético, sulfúrico diluido)",
    de: "Hoch (Phosphor-, Essig-, verdünnte Schwefelsäure)",
    fr: "Haute (Phosphorique, acétique, sulfurique dilué)",
    pt: "Alta (Fosfórico, acético, sulfúrico diluído)"
  },
  chlorideResistance: {
    en: "High (Marine environments, salt solutions)",
    zh: "高（海洋环境、盐水溶液抗点蚀）",
    es: "Alta (Ambientes marinos, soluciones salinas)",
    de: "Hoch (Marine Umgebungen, Salzlösungen)",
    fr: "Haute (Milieux marins, solutions salines)",
    pt: "Alta (Ambientes marinhos, soluções salinas)"
  },
  applications: {
    en: ["Chemical process filtration", "Polymer melt screens", "Marine strainers", "Pharmaceutical mesh"],
    zh: ["化工工艺流体过滤", "聚合物熔体挤出滤网", "船舶海水粗滤器", "医药级无菌滤网"],
    es: ["Filtración de procesos químicos", "Mallas para polímeros fundidos", "Filtros marinos", "Malla farmacéutica"],
    de: ["Chemische Prozessfiltration", "Schmelzefiltersiebe", "Marine Filterkörbe", "Pharmazeutisches Gewebe"],
    fr: ["Filtration de procédés chimiques", "Filtres pour polymères fondus", "Crépines marines", "Toiles pharmaceutiques"],
    pt: ["Filtração de processos químicos", "Telas para polímeros fundidos", "Filtros marítimos", "Telas farmacêuticas"]
  },
  badgeText: {
    en: "Chemical Grade",
    zh: "化工耐蚀级",
    es: "Grado químico",
    de: "Chemiequalität",
    fr: "Qualité chimique",
    pt: "Grau químico"
  }
}, {
  id: "310s",
  grade: "AISI 310S",
  unsCode: "S31008",
  dinCode: "1.4845",
  category: "hightemp",
  composition: "24-26% Cr, 19-22% Ni",
  corrosionLevel: "Superior",
  corrosionRating: {
    en: "Superior",
    zh: "卓越",
    es: "Superior",
    de: "Hervorragend",
    fr: "Supérieur",
    pt: "Superior"
  },
  acidResistance: {
    en: "Good (High-temp oxidation resistant)",
    zh: "优良（极高耐高温氧化腐蚀）",
    es: "Buena (Resistente a la oxidación a alta temperatura)",
    de: "Gut (Hochtemperaturoxidationsbeständig)",
    fr: "Bonne (Résistant à l'oxydation à chaud)",
    pt: "Boa (Resistente a oxidação sob alta temperatura)"
  },
  chlorideResistance: {
    en: "Moderate",
    zh: "中等",
    es: "Moderada",
    de: "Moderat",
    fr: "Modérée",
    pt: "Moderada"
  },
  applications: {
    en: ["High-temp gas filtration", "Furnace atmosphere mesh", "Calcination process screens", "Thermal processing"],
    zh: ["高温烟气粉尘截留", "工业窑炉隔热承载网", "煅烧工艺高温筛板", "热处理工艺吊篮"],
    es: ["Filtración de gas a alta temperatura", "Malla para atmósferas de horno", "Tamices de calcinación", "Tratamiento térmico"],
    de: ["Heißgasfiltration", "Ofenatmosphäre-Gewebe", "Kalzinierungssiebe", "Thermische Verfahren"],
    fr: ["Filtration de gaz chaud", "Toile pour fours industriels", "Tamis de calcination", "Traitements thermiques"],
    pt: ["Filtração de gases quentes", "Telas para fornos", "Peneiras de calcinação", "Tratamento térmico"]
  },
  badgeText: {
    en: "High Temperature",
    zh: "耐高温级",
    es: "Alta temperatura",
    de: "Hochtemperatur",
    fr: "Haute température",
    pt: "Alta temperatura"
  }
}, {
  id: "321",
  grade: "AISI 321",
  unsCode: "S32100",
  dinCode: "1.4541",
  category: "hightemp",
  composition: "17-19% Cr, 9-12% Ni + Ti Stabilized",
  corrosionLevel: "Excellent",
  corrosionRating: {
    en: "Excellent",
    zh: "优良",
    es: "Excelente",
    de: "Ausgezeichnet",
    fr: "Excellent",
    pt: "Excelente"
  },
  acidResistance: {
    en: "High (Resists intergranular attack)",
    zh: "优异（钛稳定化抗晶间腐蚀）",
    es: "Alta (Resiste el ataque intergranular)",
    de: "Hoch (Beständig gegen interkristalline Korrosion)",
    fr: "Haute (Résiste à l'attaque intergranulaire)",
    pt: "Alta (Resiste ao ataque intergranular)"
  },
  chlorideResistance: {
    en: "Good",
    zh: "良好",
    es: "Buena",
    de: "Gut",
    fr: "Bonne",
    pt: "Boa"
  },
  applications: {
    en: ["Exhaust system filtration", "Aerospace mesh components", "High-temp fluid separation"],
    zh: ["排气净化系统滤芯", "航空航天高温丝网组件", "高温热流体分离装置"],
    es: ["Filtración de sistemas de escape", "Componentes aeroespaciales", "Separación de fluidos a alta temperatura"],
    de: ["Abgassystemfiltration", "Luft- und Raumfahrtkomponenten", "Hochtemperatur-Fluidtrennung"],
    fr: ["Filtration d'échappement", "Composants aéronautiques", "Séparation de fluides à haute température"],
    pt: ["Filtração de sistemas de exaustão", "Componentes aeroespaciais", "Separação de fluidos a alta temperatura"]
  },
  badgeText: {
    en: "Ti-Stabilized",
    zh: "钛稳定型",
    es: "Estabilizado con Ti",
    de: "Ti-stabilisiert",
    fr: "Stabilisé au titane",
    pt: "Estabilizado com Ti"
  }
}, {
  id: "2205",
  grade: "Duplex 2205",
  unsCode: "S32205",
  dinCode: "1.4462",
  category: "specialty",
  composition: "22% Cr, 5% Ni, 3% Mo, 0.18% N",
  corrosionLevel: "Superior",
  corrosionRating: {
    en: "Superior",
    zh: "卓越",
    es: "Superior",
    de: "Hervorragend",
    fr: "Supérieur",
    pt: "Superior"
  },
  acidResistance: {
    en: "Superior (High stress cracking immunity)",
    zh: "极高（抗应力腐蚀开裂及氯化物腐蚀）",
    es: "Superior (Inmunidad al agrietamiento por corrosión bajo tensión)",
    de: "Hervorragend (Hohe Beständigkeit gegen Spannungsrisskorrosion)",
    fr: "Supérieure (Immunité à la corrosion sous contrainte)",
    pt: "Superior (Imunidade a corrosão sob tensão)"
  },
  chlorideResistance: {
    en: "Superior (Pitting resistance equivalent PREN > 34)",
    zh: "卓越（耐点蚀当量 PREN > 34）",
    es: "Superior (Equivalente de resistencia a picaduras PREN > 34)",
    de: "Hervorragend (Lochfraßbeständigkeit PREN > 34)",
    fr: "Supérieure (Résistance aux piqûres PREN > 34)",
    pt: "Superior (Resistência a pites PREN > 34)"
  },
  applications: {
    en: ["Oil & gas sand control screens", "Desalination filter elements", "Pulp & paper processing"],
    zh: ["石油天然气防砂筛管", "海水淡化高压过滤元件", "造纸蒸煮漂白耐氯网"],
    es: ["Telas de control de arena para petróleo y gas", "Elementos filtrantes para desalinización", "Procesamiento de pulpa y papel"],
    de: ["Öl- & Gassandschutzsiebe", "Entsalzungsfilterelemente", "Zellstoff- & Papierverarbeitung"],
    fr: ["Crépines de contrôle des sables pétrole et gaz", "Éléments filtrants pour dessalement", "Procédés pâte et papier"],
    pt: ["Telas de controle de areia em óleo e gás", "Elementos filtrantes de dessalinização", "Processamento de celulose e papel"]
  },
  badgeText: {
    en: "High Strength",
    zh: "高强双相钢",
    es: "Alta resistencia",
    de: "Hochfest",
    fr: "Haute résistance",
    pt: "Alta resistência"
  }
}, {
  id: "904l",
  grade: "904L / Hastelloy C-276",
  unsCode: "N08904 / N10276",
  dinCode: "1.4539 / 2.4819",
  category: "specialty",
  composition: "20% Cr, 25% Ni, 4.5% Mo, 1.5% Cu",
  corrosionLevel: "Exceptional",
  corrosionRating: {
    en: "Exceptional",
    zh: "特级耐蚀",
    es: "Excepcional",
    de: "Außergewöhnlich",
    fr: "Exceptionnel",
    pt: "Excepcional"
  },
  acidResistance: {
    en: "Exceptional (Concentrated sulfuric/hydrochloric)",
    zh: "顶尖（浓硫酸、盐酸与强还原酸环境）",
    es: "Excepcional (Sulfúrico y clorhídrico concentrados)",
    de: "Außergewöhnlich (Konzentrierte Schwefel-/Salzsäure)",
    fr: "Exceptionnelle (Sulfurique/chlorhydrique concentrés)",
    pt: "Excepcional (Sulfúrico e clorídrico concentrados)"
  },
  chlorideResistance: {
    en: "Exceptional (Severe brine & wet chlorine gas)",
    zh: "顶尖（高浓度盐水与湿氯气极端环境）",
    es: "Excepcional (Salmuera severa y cloro húmedo)",
    de: "Außergewöhnlich (Konzentrierte Sole und feuchtes Chlorgas)",
    fr: "Exceptionnelle (Saumure sévère et chlore gazeux humide)",
    pt: "Excepcional (Salmoura severa e gás cloro úmido)"
  },
  applications: {
    en: ["Petrochemical refining filters", "Flue gas desulfurization", "Nuclear waste processing screens"],
    zh: ["石化重整精炼滤芯", "烟气脱硫苛刻洗涤塔网", "核工业废料处理过滤网"],
    es: ["Filtros para refinación petroquímica", "Desulfuración de gases de combustión", "Mallas de tratamiento nuclear"],
    de: ["Petrochemische Raffineriefilter", "Rauchgasentschwefelung", "Nuklearabfall-Aufbereitungssiebe"],
    fr: ["Filtres de raffinage pétrochimique", "Désulfuration des fumées", "Tamis de traitement des déchets nucléaires"],
    pt: ["Filtros de refino petroquímico", "Dessulfurização de gases", "Telas de tratamento de resíduos nucleares"]
  },
  badgeText: {
    en: "Severe Service",
    zh: "重载严苛工况",
    es: "Servicio severo",
    de: "Schwerer Betrieb",
    fr: "Service sévère",
    pt: "Serviço severo"
  }
}];
const compCopy = {
  eyebrow: {
    en: "Technical Material Engineering",
    zh: "技术材料工程",
    es: "Ingeniería de materiales técnicos",
    de: "Technische Werkstofftechnik",
    fr: "Ingénierie technique des matériaux",
    pt: "Engenharia de materiais técnicos"
  },
  heading: {
    en: "Stainless steel alloy comparison guide",
    zh: "不锈钢与耐蚀合金对照指南",
    es: "Guía de comparación de aleaciones de acero inoxidable",
    de: "Vergleichsleitfaden für Edelstahllegierungen",
    fr: "Guide comparatif des alliages d'acier inoxydable",
    pt: "Guia comparativo de ligas de aço inoxidável"
  },
  lead: {
    en: "Selecting the correct wire mesh alloy ensures target flow efficiency, thermal endurance, and longevity against chemical corrosion. Review engineering specs for MutualFilter standard and specialized filter wire mesh grades.",
    zh: "正确选择丝网合金牌号有助于实现设计通量、耐温极限和抗化学腐蚀寿命。查阅 MutualFilter 标准工业级与特种耐蚀丝网牌号的技术参数。",
    es: "Seleccionar la aleación adecuada de malla metálica garantiza el caudal objetivo, la resistencia térmica y la durabilidad frente a la corrosión química. Revise las especificaciones técnicas para las calidades estándar y especializadas de MutualFilter.",
    de: "Die Wahl der richtigen Drahtgewebelegierung sichert Durchflusseffizienz, thermische Beständigkeit und Langlebigkeit gegen chemische Korrosion. Prüfen Sie die Spezifikationen der Standard- und Spezialwerkstoffe von MutualFilter.",
    fr: "Le choix du bon alliage garantit le débit cible, la tenue en température et la longévité face à la corrosion chimique. Consultez les spécifications des toiles MutualFilter standard et spéciales.",
    pt: "A seleção correta da liga de tela assegura a vazão de projeto, resistência térmica e vida útil contra corrosão química. Analise as especificações para graus padrão e especializados da MutualFilter."
  },
  card1Title: {
    en: "Molybdenum Enhancement (316 vs 304)",
    zh: "钼元素强化耐蚀（316 对比 304）",
    es: "Mejora con molibdeno (316 vs 304)",
    de: "Molybdän-Vergütung (316 vs. 304)",
    fr: "Renfort au molybdène (316 vs 304)",
    pt: "Reforço com molibdênio (316 vs 304)"
  },
  card1Desc: {
    en: "Adding 2-3% Molybdenum in SS 316 prevents localized pitting corrosion caused by chlorides and industrial process acids.",
    zh: "在 316 不锈钢中添加 2-3% 钼元素，能显著抑制氯离子与工业酸液引发的局部点蚀及缝隙腐蚀。",
    es: "Añadir 2-3% de molibdeno en SS 316 previene la corrosión localizada por picaduras provocada por cloruros y ácidos industriales.",
    de: "Die Zugabe von 2-3% Molybdän in Edelstahl 316 verhindert lokale Lochfraßkorrosion durch Chloride und Prozesssäuren.",
    fr: "L'ajout de 2 à 3 % de molybdène dans l'inox 316 prévient la corrosion par piqûres causée par les chlorures et acides industriels.",
    pt: "A adição de 2 a 3% de molibdênio no inox 316 previne a corrosão localizada por pites provocada por cloretos e ácidos industriais."
  },
  card2Title: {
    en: "High-Temperature Resistance",
    zh: "高温耐受与抗氧化",
    es: "Resistencia a alta temperatura",
    de: "Hochtemperaturbeständigkeit",
    fr: "Résistance aux hautes températures",
    pt: "Resistência a alta temperatura"
  },
  card2Desc: {
    en: "High Nickel-Chromium alloys like SS 310S retain mechanical mesh structural integrity in continuous heat environments.",
    zh: "高铬镍合金如 310S 在连续高温工作环境中仍能保持丝网结构稳定与抗氧化剥落。",
    es: "Las aleaciones de alto contenido de níquel y cromo como SS 310S conservan la integridad estructural de la malla en servicio continuo.",
    de: "Hochchrom-Nickel-Legierungen wie 310S behalten ihre Gefügesteifigkeit bei Dauerwärmebelastung.",
    fr: "Les alliages riches en chrome et nickel comme le 310S conservent l'intégrité de la toile en service continu.",
    pt: "Ligas de alto níquel e cromo como o 310S preservam a integridade estrutural da malha em serviço contínuo."
  },
  card3Title: {
    en: "EN 10204 3.1 Traceability",
    zh: "EN 10204 3.1 批次溯源",
    es: "Trazabilidad EN 10204 3.1",
    de: "EN 10204 3.1 Rückverfolgbarkeit",
    fr: "Traçabilité EN 10204 3.1",
    pt: "Rastreabilidade EN 10204 3.1"
  },
  card3Desc: {
    en: "Mill Test Reports (MTR) confirming chemical spectro-analysis and mechanical tensile limits are available when specified in the order.",
    zh: "可按订单明确要求提供原厂材质证明报告（MTR 3.1），确认材料化学成分光谱与拉伸机械性能数据。",
    es: "Los informes de prueba de fábrica (MTR) que confirman el análisis espectral y los límites de tracción están disponibles cuando se especifiquen en el pedido.",
    de: "Werksabnahmezeugnisse (MTR) zur Bestätigung von Spektralanalyse und Zugfestigkeitsgrenzen sind auftragsbezogen erhältlich.",
    fr: "Les certificats d'usine (MTR) confirmant l'analyse spectrale et les limites de traction sont disponibles sur demande à la commande.",
    pt: "Relatórios de ensaio de usina (MTR) confirmando espectroanálise e limites mecânicos estão disponíveis quando especificados no pedido."
  },
  filterAll: {
    en: "All Alloys",
    zh: "全部合金",
    es: "Todas las aleaciones",
    de: "Alle Legierungen",
    fr: "Tous les alliages",
    pt: "Todas as ligas"
  },
  filterStandard: {
    en: "General Industrial",
    zh: "通用工业级",
    es: "Industrial general",
    de: "Allgemeine Industrie",
    fr: "Industrie générale",
    pt: "Industrial geral"
  },
  filterChemical: {
    en: "Chemical & Marine",
    zh: "化工与海洋",
    es: "Química y marina",
    de: "Chemie & Marine",
    fr: "Chimie et marine",
    pt: "Química e marinha"
  },
  filterHighTemp: {
    en: "High Temperature",
    zh: "高温耐热",
    es: "Alta temperatura",
    de: "Hochtemperatur",
    fr: "Haute température",
    pt: "Alta temperatura"
  },
  filterSpecialty: {
    en: "Specialty & Severe",
    zh: "特种与严苛工况",
    es: "Especiales y severos",
    de: "Sonder- & Extrembetrieb",
    fr: "Spéciaux et sévères",
    pt: "Especiais e severos"
  },
  searchPlaceholder: {
    en: "Filter grade or application...",
    zh: "按牌号或应用筛选...",
    es: "Filtrar por grado o aplicación...",
    de: "Nach Güte oder Anwendung filtern...",
    fr: "Filtrer par nuance ou application...",
    pt: "Filtrar por grau ou aplicação..."
  },
  thGrade: {
    en: "Alloy Grade",
    zh: "合金牌号",
    es: "Calidad de aleación",
    de: "Legierungsgüte",
    fr: "Nuance d'alliage",
    pt: "Grau da liga"
  },
  thComposition: {
    en: "Composition",
    zh: "主要化学成分",
    es: "Composición",
    de: "Zusammensetzung",
    fr: "Composition",
    pt: "Composição"
  },
  thResistance: {
    en: "Chloride & Acid Resistance",
    zh: "耐氯化物与耐酸性",
    es: "Resistencia a cloruros y ácidos",
    de: "Chlorid- & Säurebeständigkeit",
    fr: "Résistance aux chlorures et acides",
    pt: "Resistência a cloretos e ácidos"
  },
  thApplications: {
    en: "Typical Mesh Applications",
    zh: "典型丝网应用",
    es: "Aplicaciones típicas de malla",
    de: "Typische Gewebeanwendungen",
    fr: "Applications types de la toile",
    pt: "Aplicações típicas da tela"
  },
  thStatus: {
    en: "Status",
    zh: "类别状态",
    es: "Estado",
    de: "Status",
    fr: "Statut",
    pt: "Status"
  },
  noMatch: {
    en: "No stainless steel grades match your search or filter selection.",
    zh: "未找到与筛选条件匹配的不锈钢或合金牌号。",
    es: "Ninguna calidad de acero inoxidable coincide con su búsqueda o filtro.",
    de: "Keine Edelstahlgüte entspricht Ihrer Suche oder Filterauswahl.",
    fr: "Aucune nuance d'acier inoxydable ne correspond à votre recherche.",
    pt: "Nenhum grau de aço inoxidável corresponde à sua busca ou filtro."
  },
  bottomHeading: {
    en: "Need raw material certification or custom alloy specs?",
    zh: "需要原材料质保文件或特种合金规格？",
    es: "¿Requiere certificación de materia prima o especificaciones a medida?",
    de: "Benötigen Sie Rohstoffzertifikate oder kundenspezifische Legierungen?",
    fr: "Besoin de certificats matière première ou de spécifications sur mesure ?",
    pt: "Necessita de certificação de matéria-prima ou especificações sob medida?"
  },
  bottomDesc: {
    en: "MutualFilter provides mill test certificates (EN 10204 3.1) and PMI positive material identification testing reports when specified in the order for woven wire mesh requirements.",
    zh: "MutualFilter 可按订单明确要求为高精度编织丝网提供原厂材质单（EN 10204 3.1）与便携式 XRF PMI 正材光谱分析报告。",
    es: "MutualFilter proporciona certificados de prueba de fábrica (EN 10204 3.1) e informes de ensayo PMI cuando se especifiquen en el pedido para mallas tejidas.",
    de: "MutualFilter stellt Werksprüfzeugnisse (EN 10204 3.1) und RFA-PMI-Materialprüfberichte auf Bestellanfrage für Präzisionsdrahtgewebe bereit.",
    fr: "MutualFilter fournit des certificats de contrôle d'usine (EN 10204 3.1) et des rapports PMI sur demande à la commande pour vos toiles métalliques.",
    pt: "A MutualFilter fornece certificados de ensaio de usina (EN 10204 3.1) e relatórios de identificação de material PMI quando especificados no pedido."
  },
  btnLibrary: {
    en: "Full Material Library",
    zh: "完整材质库",
    es: "Biblioteca completa de materiales",
    de: "Vollständige Werkstoffbibliothek",
    fr: "Bibliothèque complète des matériaux",
    pt: "Biblioteca completa de materiais"
  },
  btnRequestCert: {
    en: "Request Material Certificate",
    zh: "申请材质证明",
    es: "Solicitar certificado de material",
    de: "Materialzeugnis anfragen",
    fr: "Demander un certificat matière",
    pt: "Solicitar certificado de material"
  }
};
export default function StainlessComparison() {
  const {
    lang
  } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const filteredAlloys = useMemo(() => {
    return alloyData.filter(item => {
      const matchesCategory = activeCategory === "all" || item.category === activeCategory;
      const q = searchQuery.toLowerCase().trim();
      if (!q) return matchesCategory;
      const localizedApps = pick(lang, item.applications);
      const matchesSearch = item.grade.toLowerCase().includes(q) || item.unsCode.toLowerCase().includes(q) || localizedApps.some(app => app.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery, lang]);
  const columns: DataTableColumn<AlloySpecification>[] = useMemo(() => [{
    key: "grade",
    header: pick(lang, compCopy.thGrade),
    sticky: true,
    mobileTitle: true,
    cell: row => <div className="flex flex-col gap-1">
          <span className="text-sm font-bold text-foreground">
            {row.grade}
          </span>
          <span className="mf-caption text-muted-foreground font-mono">
            UNS {row.unsCode.split(" / ")[0]}
          </span>
          <span className="mf-caption text-muted-foreground font-mono">
            DIN {row.dinCode.split(" / ")[0]}
          </span>
        </div>
  }, {
    key: "composition",
    header: pick(lang, compCopy.thComposition),
    cell: row => <span className="mf-caption text-foreground font-mono leading-relaxed">
          {row.composition}
        </span>
  }, {
    key: "resistance",
    header: pick(lang, compCopy.thResistance),
    cell: row => <div className="space-y-1 text-sm">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full shrink-0 bg-primary" />
            <span className="font-semibold text-foreground">
              {pick(lang, row.corrosionRating)}
            </span>
          </div>
          <p className="mf-caption text-muted-foreground leading-tight">
            {pick(lang, row.chlorideResistance)}
          </p>
        </div>
  }, {
    key: "applications",
    header: pick(lang, compCopy.thApplications),
    cell: row => <div className="flex flex-wrap gap-1">
          {pick(lang, row.applications).map((app, appIdx) => <span key={appIdx} className="inline-flex items-center px-2 py-0.5 rounded mf-caption bg-muted text-foreground border border-border">
              {app}
            </span>)}
        </div>
  }, {
    key: "status",
    header: pick(lang, compCopy.thStatus),
    align: "right",
    cell: row => <Badge variant="outline" className="mf-caption uppercase tracking-wider font-semibold">
          {pick(lang, row.badgeText)}
        </Badge>
  }], [lang]);
  return <section id="stainlesscomparison" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground">
                {pick(lang, compCopy.eyebrow)}
              </span>
            </div>
            <h2 className="mf-h2 text-foreground text-balance">
              {pick(lang, compCopy.heading)}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
              {pick(lang, compCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Highlight Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          <Reveal delay={0}>
            <div className="bg-card border border-border rounded-lg p-5 flex items-start gap-4 h-full">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-card-foreground">
                  {pick(lang, compCopy.card1Title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-1 leading-normal">
                  {pick(lang, compCopy.card1Desc)}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={60}>
            <div className="bg-card border border-border rounded-lg p-5 flex items-start gap-4 h-full">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                <Thermometer className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-card-foreground">
                  {pick(lang, compCopy.card2Title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-1 leading-normal">
                  {pick(lang, compCopy.card2Desc)}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-card border border-border rounded-lg p-5 flex items-start gap-4 h-full">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                <CircleCheck className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-card-foreground">
                  {pick(lang, compCopy.card3Title)}
                </h3>
                <p className="mf-caption text-muted-foreground mt-1 leading-normal">
                  {pick(lang, compCopy.card3Desc)}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Filter Controls & Search */}
        <Reveal delay={60}>
          <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-muted/40 p-4 rounded-lg border border-border">
            {/* Category Tabs */}
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => setActiveCategory("all")} className={`px-3 py-2 h-11 text-sm font-medium transition-colors ${activeCategory === "all" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted border border-border"}`}>
                {pick(lang, compCopy.filterAll)} ({alloyData.length})
              </Button>
              <Button onClick={() => setActiveCategory("standard")} className={`px-3 py-2 h-11 text-sm font-medium transition-colors ${activeCategory === "standard" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted border border-border"}`}>
                {pick(lang, compCopy.filterStandard)}
              </Button>
              <Button onClick={() => setActiveCategory("chemical")} className={`px-3 py-2 h-11 text-sm font-medium transition-colors ${activeCategory === "chemical" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted border border-border"}`}>
                {pick(lang, compCopy.filterChemical)}
              </Button>
              <Button onClick={() => setActiveCategory("hightemp")} className={`px-3 py-2 h-11 text-sm font-medium transition-colors ${activeCategory === "hightemp" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted border border-border"}`}>
                {pick(lang, compCopy.filterHighTemp)}
              </Button>
              <Button onClick={() => setActiveCategory("specialty")} className={`px-3 py-2 h-11 text-sm font-medium transition-colors ${activeCategory === "specialty" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted border border-border"}`}>
                {pick(lang, compCopy.filterSpecialty)}
              </Button>
            </div>

            {/* Search Bar */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input type="text" placeholder={pick(lang, compCopy.searchPlaceholder)} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 h-11 text-sm bg-card border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground" />
            </div>
          </div>
        </Reveal>

        {/* Data Table */}
        <Reveal delay={120}>
          <div className="mt-6">
            <DataTable<AlloySpecification> columns={columns} rows={filteredAlloys} getRowKey={row => row.id} empty={pick(lang, compCopy.noMatch)} minWidth={720} />
          </div>
        </Reveal>

        {/* Quality Certificate Notice Banner */}
        <Reveal delay={180}>
          <div className="mt-10 rounded-lg bg-card border border-border p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0 mt-1 sm:mt-0">
                <FileText className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="mf-h3 text-card-foreground">
                  {pick(lang, compCopy.bottomHeading)}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                  {pick(lang, compCopy.bottomDesc)}
                </p>
              </div>
            </div>

            {/* Action CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
              <Button variant="outline" asChild className="w-full sm:w-auto h-11">
                <Link to="/StainlessSteelFilterMeshMaterialsPage">
                  <FileText className="size-4 mr-2" aria-hidden="true" />
                  <span>{pick(lang, compCopy.btnLibrary)}</span>
                </Link>
              </Button>

              <Button variant="default" asChild className="w-full sm:w-auto h-11">
                <Link to="/RequestAQuotePage">
                  <span>{pick(lang, compCopy.btnRequestCert)}</span>
                  <ArrowRight className="size-4 ml-2" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}